import {
  useState,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
  useMemo
} from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import HTMLFlipBook from 'react-pageflip'
import { PageWrapper, ButtonBar, ButtonWrapper } from './style'
import Modal from 'react-bootstrap/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faX } from '@fortawesome/pro-duotone-svg-icons/faX'
import { faArrowDownToLine } from '@fortawesome/pro-solid-svg-icons/faArrowDownToLine'
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft'
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner'
import './style.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

const MOBILE_MAX_WIDTH_PX = 576

const FlipBook = forwardRef(
  ({ pdfFile, resetFlipBook, url, allowDownload = true }, ref) => {
    let windowScreenHeight = null
    let windowScreenWidth = null
    let windowInnerWidth = null
    let windowInnerHeight = null

    if (typeof window !== 'undefined') {
      windowInnerWidth = window.innerWidth
      windowInnerHeight = window.innerHeight
      windowScreenHeight = window.screen.height
      windowScreenWidth = window.screen.width
    }

    const maxWidth = Math.round(windowScreenHeight * 0.8)
    const maxHeight = Math.round(windowScreenHeight * 0.8)
    const minWidth = 360
    const minHeight = 400
    const defaultWidth = 700

    const [show, setShow] = useState(false)
    const [pagesLoading, setPagesLoading] = useState(true)
    const [numPages, setNumPages] = useState(null)
    const [pageDimensions, setPageDimensions] = useState({
      width: Math.min(
        windowInnerWidth * 0.46 < minWidth
          ? windowInnerWidth * 0.46 * 2
          : windowInnerWidth * 0.46,
        defaultWidth
      ),
      height: Math.min(windowInnerHeight * 0.93, defaultWidth)
    })
    const [pageNumber, setPageNumber] = useState(0)
    const [mobilePageIndex, setMobilePageIndex] = useState(0)
    const [bookDimensions, setBookDimensions] = useState([])
    const [bookSize, setBookSize] = useState({})
    const prevWidth = useRef(windowInnerWidth)

    const [isMobileView, setIsMobileView] = useState(
      () =>
        typeof window !== 'undefined' &&
        window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH_PX - 1}px)`).matches
    )

    useEffect(() => {
      if (typeof window === 'undefined') return
      const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH_PX - 1}px)`)
      const onChange = () => setIsMobileView(mq.matches)
      mq.addEventListener('change', onChange)
      return () => mq.removeEventListener('change', onChange)
    }, [])

    const book = useRef(null)
    const bookDiv = useRef(null)
    // const timeout = useRef(null)
    const documentRef = useRef(null)

    useImperativeHandle(ref, () => ({
      setShow
    }))

    useEffect(() => {
      return () => {
        book.current?.pageFlip().destroy()
        documentRef.current?.linkService.current.pdfDocument.cleanup(false)
      }
    }, [])

    const handleClose = () => {
      resetFlipBook()
    }

    function onDocumentLoadSuccess({ numPages }) {
      setNumPages(numPages)
      setMobilePageIndex(0)
      setPageDimensions(getSize())
      setPagesLoading(true)
    }

    const debounce = (func, wait) => {
      const timeout = { current: null }
      return (...args) => {
        clearTimeout(timeout.current)
        timeout.current = setTimeout(() => func(...args), wait)
      }
    }

    const getSize = () => {
      if (typeof window !== 'undefined') {
        const currentWidth = window.innerWidth
        const isNarrow = currentWidth < MOBILE_MAX_WIDTH_PX
        const rect = bookDiv.current?.getBoundingClientRect()
        const containerW = rect && rect.width > 0 ? rect.width : currentWidth
        let newWidth = Math.min(containerW * (isNarrow ? 1 : 0.5), maxWidth)
        if (
          (prevWidth.current >= 870 && currentWidth < 870) ||
          (prevWidth.current < 870 && currentWidth >= 870)
        ) {
          setTimeout(() => window.dispatchEvent(new Event('resize')), 100)
        } else if (
          (prevWidth.current >= MOBILE_MAX_WIDTH_PX &&
            currentWidth < MOBILE_MAX_WIDTH_PX) ||
          (prevWidth.current < MOBILE_MAX_WIDTH_PX &&
            currentWidth >= MOBILE_MAX_WIDTH_PX)
        ) {
          setTimeout(() => window.dispatchEvent(new Event('resize')), 100)
        }
        prevWidth.current = currentWidth

        if (newWidth < minWidth) {
          newWidth = isNarrow
            ? Math.max(Math.min(containerW || currentWidth, maxWidth), minWidth)
            : Math.max(Math.min(newWidth * 2, maxWidth), minWidth)
        }

        return { width: newWidth }
      }
    }

    useLayoutEffect(() => {
      if (typeof window !== 'undefined') {
        const handleResize = () => {
          if (bookDiv.current) {
            setPageDimensions(getSize())
          }
        }
        const debouncedHandleResize = debounce(handleResize, 100)
        window.addEventListener('resize', debouncedHandleResize)

        return () => window.removeEventListener('resize', debouncedHandleResize)
      }
    }, [])

    useLayoutEffect(() => {
      if (Object.keys(bookDimensions).length > 0) {
        setBookSize({
          width: bookDimensions.width,
          height: bookDimensions.height
        })
      }
    }, [bookDimensions])

    const goToPrevPage = () => {
      if (isMobileView && numPages > 1) {
        setMobilePageIndex((i) => Math.max(0, i - 1))
        return
      }
      try {
        book.current?.pageFlip().flipPrev()
        setPageNumber(book.current?.pageFlip().getCurrentPageIndex())
      } catch (e) {
        console.error('Too fast pressing prev button', e)
      }
    }

    const goToNextPage = () => {
      if (isMobileView && numPages > 1) {
        setMobilePageIndex((i) => Math.min((numPages ?? 1) - 1, i + 1))
        return
      }
      try {
        book.current?.pageFlip().flipNext()
        setPageNumber(book.current?.pageFlip().getCurrentPageIndex())
      } catch (e) {
        console.error('Too fast pressing next button', e)
      }
    }

    const onRenderSuccess = (size, pageNumber) => {
      if (pageNumber <= 2 || isMobileView) {
        setBookDimensions(size)
      }
      setPagesLoading(false)
    }

    const showPrevNav =
      numPages > 1 &&
      (isMobileView ? mobilePageIndex > 0 : numPages > 2 && pageNumber !== 0)

    const showNextNav =
      numPages > 1 &&
      (isMobileView
        ? mobilePageIndex < numPages - 1
        : numPages > 2 && pageNumber + 2 < numPages)

    const VIRTUALIZATION_WINDOW = 4
    const pages = useMemo(() => {
      if (!numPages || isMobileView) return []
      return Array.from({ length: numPages }, (_, index) => {
        if (Math.abs(index - pageNumber) <= VIRTUALIZATION_WINDOW) {
          return (
            <PageWrapper key={`page_${index + 1}`} pageIndex={index + 1}>
              <Page
                className="page-content"
                pageNumber={index + 1}
                onRenderSuccess={(size) => onRenderSuccess(size, index + 1)}
                width={pageDimensions.width}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            </PageWrapper>
          )
        } else {
          return <PageWrapper key={`page_${index + 1}`} pageIndex={index + 1} />
        }
      })
    }, [numPages, pageNumber, pageDimensions.width, isMobileView])

    const mobilePdfCanvasWidth =
      typeof window !== 'undefined'
        ? Math.max(120, document.documentElement.clientWidth)
        : pageDimensions.width

    return (
      <Modal
        fullscreen
        show={show}
        onHide={handleClose}
        className="custom-modal"
      >
        <Modal.Body
          className={isMobileView ? 'flipbook-modal-body-mobile' : undefined}
          style={{ overflowX: 'auto', maxHeight: windowScreenHeight }}
        >
          {pagesLoading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1
              }}
            >
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: '48px', color: '#ffffff' }}
              />
            </div>
          )}
          <div
            style={{
              minWidth: windowInnerWidth < 870 ? '' : windowScreenWidth * 0.73,
              position: 'relative',
              paddingRight: windowInnerWidth < 576 ? '0px' : '60px',
              paddingLeft: windowInnerWidth < 576 ? '0px' : '60px',
              visibility: pagesLoading ? 'hidden' : 'visible'
            }}
          >
            {numPages === 1 ? (
              <div
                className={`page-flip-container${isMobileView ? ' flipbook-touch-zoom flipbook-mobile-page-root' : ''}`}
                ref={bookDiv}
                style={{
                  display: 'flex',
                  justifyContent: isMobileView ? 'flex-start' : 'center',
                  alignItems: isMobileView ? 'flex-start' : 'center',
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  overflow: 'auto',
                  background: 'transparent'
                }}
              >
                {pdfFile && (
                  <Document
                    ref={documentRef}
                    file={pdfFile}
                    onLoadSuccess={onDocumentLoadSuccess}
                    style={{
                      display: 'flex',
                      justifyContent: isMobileView ? 'flex-start' : 'center',
                      alignItems: isMobileView ? 'flex-start' : 'center',
                      width: '100%',
                      height: '100%'
                    }}
                  >
                    <Page
                      className={`page-content${isMobileView ? ' flipbook-mobile-page-canvas' : ''}`}
                      pageNumber={1}
                      width={
                        isMobileView && typeof window !== 'undefined'
                          ? Math.max(120, document.documentElement.clientWidth)
                          : Math.min(
                              pageDimensions.width * 2,
                              windowInnerWidth * 0.98
                            )
                      }
                      height={pageDimensions.height}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      onRenderSuccess={(size) => onRenderSuccess(size, 1)}
                    />
                  </Document>
                )}
              </div>
            ) : numPages > 1 && isMobileView ? (
              <div
                className="page-flip-container flipbook-touch-zoom flipbook-mobile-page-root"
                ref={bookDiv}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  position: 'relative',
                  width: '100%',
                  minHeight: '40vh',
                  overflow: 'auto',
                  background: 'transparent'
                }}
              >
                {pdfFile && (
                  <div className="flipbook-mobile-page-shell">
                    <Document
                      ref={documentRef}
                      file={pdfFile}
                      onLoadSuccess={onDocumentLoadSuccess}
                      style={{
                        display: 'block',
                        width: '100%',
                        maxWidth: '100%'
                      }}
                    >
                      <Page
                        key={mobilePageIndex}
                        className="page-content flipbook-mobile-page-canvas"
                        pageNumber={mobilePageIndex + 1}
                        width={mobilePdfCanvasWidth}
                        renderAnnotationLayer={false}
                        renderTextLayer={false}
                        onRenderSuccess={(size) =>
                          onRenderSuccess(size, mobilePageIndex + 1)
                        }
                      />
                    </Document>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="page-flip-container"
                ref={bookDiv}
                style={{ overflow: 'hidden' }}
              >
                {pdfFile && (
                  <Document
                    ref={documentRef}
                    file={pdfFile}
                    onLoadSuccess={onDocumentLoadSuccess}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <HTMLFlipBook
                      key={
                        bookSize.width?.toString() +
                        '-' +
                        bookSize.height?.toString()
                      }
                      width={bookSize.width || defaultWidth}
                      height={bookSize.height || defaultWidth}
                      ref={book}
                      size="stretch"
                      minWidth={minWidth}
                      maxWidth={maxWidth}
                      minHeight={minHeight}
                      maxHeight={maxHeight}
                      onFlip={(pageNumber) => setPageNumber(pageNumber.data)}
                      maxShadowOpacity={0.5}
                      mobileScrollSupport
                      showCover={numPages >= 3}
                    >
                      {pages}
                    </HTMLFlipBook>
                  </Document>
                )}
              </div>
            )}
            <ButtonBar>
              {showPrevNav && (
                <ButtonWrapper
                  onClick={goToPrevPage}
                  height={bookSize.height || defaultWidth}
                  type="left"
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </ButtonWrapper>
              )}
              {showNextNav && (
                <ButtonWrapper
                  onClick={goToNextPage}
                  height={bookSize.height || defaultWidth}
                  type="right"
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </ButtonWrapper>
              )}
              <ButtonWrapper onClick={handleClose} type="top-right">
                <FontAwesomeIcon icon={faX} />
              </ButtonWrapper>
              {allowDownload && (
                <ButtonWrapper type="top-right-2" href={url} download>
                  <FontAwesomeIcon icon={faArrowDownToLine} />
                </ButtonWrapper>
              )}
            </ButtonBar>
          </div>
        </Modal.Body>
      </Modal>
    )
  }
)
FlipBook.displayName = 'FlipBook'

export default FlipBook
